import React, { useEffect, useMemo, useRef } from 'react';
import type { StrokeSample } from '../../../features/dashboards/rowingArcTypes';
import { calculateStatus, type MarkerStatus, type SessionAverages } from './arcMetrics';
import { ARC_INTERACTION, ARC_STRUCTURE, ARC_TARGET, getStatusBgClass } from './arcVisualSystem';

interface StrokeSequenceBarsProps {
  sessionAverages: SessionAverages;
  seatStrokes: StrokeSample[];
  activeStrokeIndex: number;
  onStrokeClick?: (strokeIndex: number) => void;
  targets: {
    catchGapMax: number;
    finishSlipMin: number;
    finishSlipMax: number;
    effectiveLengthMin: number;
    effectiveLengthMax: number;
    warningBand: number;
  };
}

type MetricKey = 'catchGap' | 'effectiveLength' | 'finishSlip';

const BAR_MAX_HEIGHT = 128;
const STROKE_GROUP_WIDTH = 34;
const STROKE_LABEL_STEP = 10;
const TIME_LABEL_STEP_SEC = 10;

const metricMeta: Record<
  MetricKey,
  { label: string; guideTone: 'red' | 'green' | 'yellow'; hint: string }
> = {
  catchGap: {
    label: 'Catch Gap',
    guideTone: 'red',
    hint: 'Stay below the ceiling',
  },
  effectiveLength: {
    label: 'Effective Length',
    guideTone: 'green',
    hint: 'Stay inside the target band',
  },
  finishSlip: {
    label: 'Finish Slip',
    guideTone: 'yellow',
    hint: 'Stay inside the target band',
  },
};

const clampPercent = (value: number, min: number, max: number) =>
  Math.max(0, Math.min(100, ((value - min) / Math.max(1e-6, max - min)) * 100));

const getBarCenterPx = (strokeIndex: number) =>
  strokeIndex * STROKE_GROUP_WIDTH + (STROKE_GROUP_WIDTH - 6) / 2;

const statusText: Record<MarkerStatus, string> = {
  good: 'In target',
  caution: 'Near edge',
  bad: 'Out of target',
  na: 'Not measured',
};

const formatAverage = (value: number | null) => (value == null ? '—' : `${value.toFixed(1)}°`);

const SessionAverageItem: React.FC<{
  label: string;
  value: string;
  detail: string;
}> = ({ label, value, detail }) => (
  <div>
    <div className="text-[10px] font-semibold uppercase tracking-wide text-rowing-gray-500">
      {label}
    </div>
    <div className="mt-1 text-lg font-semibold text-rowing-gray-900">{value}</div>
    <div className="text-[11px] text-rowing-gray-500">{detail}</div>
  </div>
);

const StrokeSequenceBars: React.FC<StrokeSequenceBarsProps> = ({
  sessionAverages,
  seatStrokes,
  activeStrokeIndex,
  onStrokeClick,
  targets,
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const sequence = useMemo(() => {
    const knownDurations = seatStrokes
      .map((stroke) =>
        stroke.driveTimeMs != null && stroke.recoveryTimeMs != null
          ? stroke.driveTimeMs + stroke.recoveryTimeMs
          : null
      )
      .filter((duration): duration is number => duration != null);
    const fallbackDurationMs =
      knownDurations.length > 0
        ? knownDurations.reduce((sum, duration) => sum + duration, 0) / knownDurations.length
        : 2000;

    let elapsedMs = 0;

    return seatStrokes.map((stroke) => {
      const effectiveLength =
        stroke.catchAngleDeg + stroke.finishAngleDeg - stroke.catchSlipDeg - stroke.finishSlipDeg;
      const durationMs =
        stroke.driveTimeMs != null && stroke.recoveryTimeMs != null
          ? stroke.driveTimeMs + stroke.recoveryTimeMs
          : fallbackDurationMs;
      const sequencedStroke = {
        strokeIndex: stroke.strokeIndex,
        strokeNumber: stroke.strokeIndex + 1,
        catchGap: stroke.catchSlipDeg,
        catchGapApplicable:
          stroke.catchSlipApplicable !== false && stroke.catchSlipConfidence !== 'low',
        effectiveLength,
        finishSlip: stroke.finishSlipDeg,
        startMs: elapsedMs,
        durationMs,
      };
      elapsedMs += durationMs;
      return sequencedStroke;
    });
  }, [seatStrokes]);

  const totalDurationMs = sequence.length
    ? sequence[sequence.length - 1].startMs + sequence[sequence.length - 1].durationMs
    : 0;

  const strokeTicks = useMemo(
    () =>
      sequence.filter(
        (stroke, index) => index === 0 || stroke.strokeNumber % STROKE_LABEL_STEP === 0
      ),
    [sequence]
  );

  const timeTicks = useMemo(() => {
    if (sequence.length === 0 || totalDurationMs <= 0) {
      return [];
    }

    const ticks: Array<{ left: number; label: string; strokeNumber: number }> = [];
    for (
      let second = 0;
      second <= Math.floor(totalDurationMs / 1000);
      second += TIME_LABEL_STEP_SEC
    ) {
      const targetMs = second * 1000;
      const stroke =
        sequence.find(
          (entry) => targetMs >= entry.startMs && targetMs < entry.startMs + entry.durationMs
        ) ?? sequence[sequence.length - 1];
      ticks.push({
        left: getBarCenterPx(stroke.strokeIndex),
        label: `${second}s`,
        strokeNumber: stroke.strokeNumber,
      });
    }

    return ticks;
  }, [sequence, totalDurationMs]);

  const domains = useMemo(() => {
    const buildWindow = (values: number[], targetValues: number[], padding: number, floor = 0) => {
      const rawMin = Math.min(...values, ...targetValues);
      const rawMax = Math.max(...values, ...targetValues);
      const min = Math.max(floor, Math.floor((rawMin - padding) * 10) / 10);
      const max = Math.ceil((rawMax + padding) * 10) / 10;

      return { min, max };
    };

    const catchGapValues = sequence.filter((s) => s.catchGapApplicable).map((s) => s.catchGap);

    return {
      catchGap: buildWindow(
        catchGapValues.length === 0 ? [0] : catchGapValues,
        [targets.catchGapMax],
        1.5,
        0
      ),
      effectiveLength: buildWindow(
        sequence.map((stroke) => stroke.effectiveLength),
        [targets.effectiveLengthMin, targets.effectiveLengthMax],
        2.5,
        0
      ),
      finishSlip: buildWindow(
        sequence.map((stroke) => stroke.finishSlip),
        [targets.finishSlipMax],
        1.5,
        0
      ),
    };
  }, [sequence, targets]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container || sequence.length === 0) {
      return;
    }

    const centerOffset =
      activeStrokeIndex * STROKE_GROUP_WIDTH - container.clientWidth / 2 + STROKE_GROUP_WIDTH;
    container.scrollTo({
      left: Math.max(0, centerOffset),
      behavior: 'smooth',
    });
  }, [activeStrokeIndex, sequence.length]);

  const targetGuideConfig: Record<
    MetricKey,
    {
      ceiling?: number;
      band?: { min: number; max: number; tone: 'green' | 'yellow' };
    }
  > = {
    catchGap: { ceiling: targets.catchGapMax },
    effectiveLength: {
      band: {
        min: targets.effectiveLengthMin,
        max: targets.effectiveLengthMax,
        tone: 'green',
      },
    },
    finishSlip: { ceiling: targets.finishSlipMax },
  };

  const getGuideBottom = (metric: MetricKey, value: number) =>
    `${clampPercent(value, domains[metric].min, domains[metric].max)}%`;

  const getBandHeight = (metric: MetricKey, min: number, max: number) =>
    `${Math.max(
      0,
      clampPercent(max, domains[metric].min, domains[metric].max) -
        clampPercent(min, domains[metric].min, domains[metric].max)
    )}%`;

  const getBandBottom = (metric: MetricKey, min: number) =>
    `${clampPercent(min, domains[metric].min, domains[metric].max)}%`;

  const getAxisLabel = (metric: MetricKey) => {
    if (metric === 'catchGap') {
      return `Max ${targets.catchGapMax.toFixed(1)}°`;
    }
    if (metric === 'effectiveLength') {
      return `${targets.effectiveLengthMin.toFixed(1)}°-${targets.effectiveLengthMax.toFixed(1)}°`;
    }
    return `Max ${targets.finishSlipMax.toFixed(1)}°`;
  };

  type SeqStroke = (typeof sequence)[number];

  const getMetricStatus = (metric: MetricKey, stroke: SeqStroke): MarkerStatus => {
    const value = stroke[metric];
    if (metric === 'catchGap' && !stroke.catchGapApplicable) {
      return 'na';
    }

    if (metric === 'catchGap') {
      return calculateStatus(value, undefined, targets.catchGapMax, targets.warningBand);
    }

    if (metric === 'effectiveLength') {
      return calculateStatus(
        value,
        targets.effectiveLengthMin,
        targets.effectiveLengthMax,
        targets.warningBand
      );
    }

    return calculateStatus(value, undefined, targets.finishSlipMax, targets.warningBand);
  };

  return (
    <section className="rounded-lg border border-border bg-surface p-4 shadow-sm">
      <div className="mb-4 flex flex-col gap-3">
        <div>
          <h3 className="text-sm font-semibold text-rowing-gray-900">Stroke Sequence</h3>
          <p className="text-xs text-rowing-gray-600">
            Each row uses its own labeled viewing window so stroke-to-stroke variation stays
            visible. The top cap shows whether that stroke is in target, near the edge, or out.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 rounded-lg border border-rowing-gray-200 bg-rowing-gray-50/80 p-3 sm:grid-cols-4">
          <SessionAverageItem
            label="Strokes"
            value={`${sessionAverages.totalStrokeCount}`}
            detail="in selected seat"
          />
          <SessionAverageItem
            label="Catch Gap"
            value={formatAverage(sessionAverages.catchGap)}
            detail={`${sessionAverages.measuredCatchGapCount} measured`}
          />
          <SessionAverageItem
            label="Effective Length"
            value={formatAverage(sessionAverages.effectiveLength)}
            detail="whole session · selected seat"
          />
          <SessionAverageItem
            label="Finish Slip"
            value={formatAverage(sessionAverages.finishSlip)}
            detail="whole session · selected seat"
          />
        </div>
      </div>

      <div className="grid grid-cols-[120px_1fr] gap-4 items-start">
        <div className="pt-7 space-y-6">
          {(Object.keys(metricMeta) as MetricKey[]).map((metric) => (
            <div key={metric} className="h-36 flex flex-col justify-between">
              <div>
                <div className="text-xs font-semibold text-rowing-gray-800">
                  {metricMeta[metric].label}
                </div>
                <div className="text-[11px] text-rowing-gray-500">{metricMeta[metric].hint}</div>
                <div className="mt-1 text-[11px] text-rowing-gray-400">{getAxisLabel(metric)}</div>
              </div>
              <div className="flex justify-between text-[11px] text-rowing-gray-400">
                <span>{domains[metric].min.toFixed(1)}°</span>
                <span>{domains[metric].max.toFixed(1)}°</span>
              </div>
            </div>
          ))}
          <div className="h-12 flex flex-col justify-center gap-2">
            <div className="text-[11px] font-medium text-rowing-gray-600">Stroke</div>
            <div className="text-[11px] font-medium text-rowing-gray-600">Time</div>
          </div>
        </div>

        <div ref={scrollRef} className="overflow-x-auto overflow-y-hidden pb-1">
          <div
            className="relative min-w-full"
            style={{ width: `${Math.max(sequence.length * STROKE_GROUP_WIDTH, 640)}px` }}
          >
            <div className="grid grid-rows-3 gap-6">
              {(Object.keys(metricMeta) as MetricKey[]).map((metric) => {
                const guide = targetGuideConfig[metric];

                return (
                  <div
                    key={metric}
                    className="relative rounded-lg border border-rowing-gray-200 bg-rowing-gray-50/60 px-3 pt-3 pb-2"
                    style={{ height: `${BAR_MAX_HEIGHT + 18}px` }}
                  >
                    <div className="absolute inset-x-3 top-3 bottom-3">
                      <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                        <div className="border-t border-rowing-gray-100" />
                        <div className="border-t border-rowing-gray-100" />
                        <div className="border-t border-rowing-gray-100" />
                        <div className="border-t border-rowing-gray-100" />
                      </div>

                      {guide.ceiling !== undefined && (
                        <div
                          className={`absolute left-0 right-0 border-t-2 ${ARC_TARGET.line.catchGap.borderClass} opacity-80`}
                          style={{ bottom: getGuideBottom(metric, guide.ceiling) }}
                        />
                      )}

                      {guide.band && (
                        <>
                          <div
                            className={`absolute left-0 right-0 rounded-sm border-y-2 opacity-20 ${
                              guide.band.tone === 'green'
                                ? `${ARC_TARGET.band.effectiveLength.borderClass} ${ARC_TARGET.band.effectiveLength.bgClass}`
                                : `${ARC_TARGET.band.finishSlip.borderClass} ${ARC_TARGET.band.finishSlip.bgClass}`
                            }`}
                            style={{
                              bottom: getBandBottom(metric, guide.band.min),
                              height: getBandHeight(metric, guide.band.min, guide.band.max),
                            }}
                          />
                          <div
                            className={`absolute left-0 right-0 border-t-2 opacity-80 ${
                              guide.band.tone === 'green'
                                ? ARC_TARGET.band.effectiveLength.lineClass
                                : ARC_TARGET.band.finishSlip.lineClass
                            }`}
                            style={{ bottom: getGuideBottom(metric, guide.band.min) }}
                          />
                          <div
                            className={`absolute left-0 right-0 border-t-2 opacity-80 ${
                              guide.band.tone === 'green'
                                ? ARC_TARGET.band.effectiveLength.lineClass
                                : ARC_TARGET.band.finishSlip.lineClass
                            }`}
                            style={{ bottom: getGuideBottom(metric, guide.band.max) }}
                          />
                        </>
                      )}
                    </div>

                    <div className="absolute left-3 right-3 bottom-3 border-t border-rowing-gray-200" />

                    <div className="flex items-end h-full gap-[6px]">
                      {sequence.map((stroke, index) => {
                        const value = stroke[metric];
                        const isCatchGapNa = metric === 'catchGap' && !stroke.catchGapApplicable;
                        const heightPercent = isCatchGapNa
                          ? 0
                          : clampPercent(value, domains[metric].min, domains[metric].max);
                        const isActive = index === activeStrokeIndex;
                        const metricStatus = getMetricStatus(metric, stroke);

                        return (
                          <button
                            key={`${metric}-${stroke.strokeIndex}`}
                            type="button"
                            onClick={() => onStrokeClick?.(index)}
                            className={`relative flex items-end justify-center rounded-sm border transition-all ${
                              isActive
                                ? `${ARC_INTERACTION.activeBorderClass} ${ARC_INTERACTION.activeSoftBgClass}`
                                : 'border-transparent hover:border-rowing-gray-300 hover:bg-white/60'
                            }`}
                            style={{
                              width: `${STROKE_GROUP_WIDTH - 6}px`,
                              height: `${BAR_MAX_HEIGHT}px`,
                            }}
                            title={`Stroke ${stroke.strokeIndex + 1}: ${metricMeta[metric].label} ${
                              isCatchGapNa ? 'N/A' : `${value.toFixed(1)}°`
                            } (${statusText[metricStatus]})`}
                          >
                            <span
                              className={`absolute left-1/2 top-1 h-1.5 w-3 -translate-x-1/2 rounded-full ${getStatusBgClass(metricStatus)}`}
                            />
                            <span
                              className={`w-4 rounded-t-sm ${ARC_STRUCTURE.neutralBarClass}`}
                              style={{
                                height: `${heightPercent}%`,
                                minHeight: value > 0 ? '2px' : '0',
                              }}
                            />
                            {isActive && (
                              <span
                                className={`absolute -top-2 h-2 w-[2px] rounded-full ${ARC_INTERACTION.activeBgClass}`}
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-3 flex items-center gap-4 text-xs text-rowing-gray-600 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm bg-rowing-gray-500/85" />
                <span>Bar = actual angle</span>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`inline-block h-[2px] w-5 ${ARC_TARGET.line.catchGap.lineClass}`}
                />
                <span>Catch Gap ceiling</span>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`inline-block h-3 w-5 rounded-sm border ${ARC_TARGET.band.effectiveLength.borderClass} ${ARC_TARGET.band.effectiveLength.bgClass}`}
                />
                <span>Target band</span>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`inline-block h-1.5 w-3 rounded-full ${getStatusBgClass('good')}`}
                />
                <span>Status cap = in / edge / out</span>
              </div>
            </div>

            <div className="mt-3 border-t border-rowing-gray-200 pt-3 space-y-2">
              <div className="relative h-8" style={{ marginLeft: '12px', marginRight: '12px' }}>
                <div className="absolute inset-x-0 top-0 border-t border-rowing-gray-200" />
                {strokeTicks.map((stroke) => (
                  <div
                    key={`stroke-tick-${stroke.strokeNumber}`}
                    className="absolute top-0 -translate-x-1/2"
                    style={{
                      left: `${getBarCenterPx(stroke.strokeIndex)}px`,
                    }}
                  >
                    <span className="block h-2 w-px bg-rowing-gray-300 mx-auto" />
                    <span className="mt-1 block text-center text-[11px] text-rowing-gray-500 whitespace-nowrap">
                      {stroke.strokeNumber}
                    </span>
                  </div>
                ))}
              </div>
              <div className="relative h-8" style={{ marginLeft: '12px', marginRight: '12px' }}>
                <div className="absolute inset-x-0 top-0 border-t border-rowing-gray-200" />
                {timeTicks.map((tick) => (
                  <div
                    key={`time-tick-${tick.label}-${tick.strokeNumber}`}
                    className="absolute top-0 -translate-x-1/2"
                    style={{ left: `${tick.left}px` }}
                  >
                    <span className="block h-2 w-px bg-rowing-gray-300 mx-auto" />
                    <span className="mt-1 block text-center text-[11px] text-rowing-gray-500 whitespace-nowrap">
                      {tick.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StrokeSequenceBars;
